import React, { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { useQueryParams, StringParam, withDefault } from 'use-query-params';

import { useUser } from 'src/hooks/use-user';
import useErnie from 'shared/hooks/use-ernie';
import PublicEnv from 'shared/utils/public-env';

const queryParamsConfig = { deepLinkSegment: withDefault(StringParam, '') };
const consumerRedirectUrl = `${PublicEnv.consumerUrl}/dispensaries`;

// This account is a consumer-type account with no dispensary-admin access, so
// useConsumerAppSwitchEffect (below) logs it out and bounces it to the consumer app.
// That redirect is intentional access-control, not a bug -- but without this message
// it happens silently, leaving a misconfigured admin user with no explanation for why
// they were logged out. Show a brief toast before the cross-app navigation so there's
// at least some visible signal of what happened and why.
const NO_ADMIN_ACCESS_MESSAGE =
  "This account doesn't have access to the admin dashboard. Redirecting you to Dutchie...";

// Give the ernie toast a moment to render before we navigate away to a different
// app/origin -- window.location.href unloads this page immediately, so without a
// short delay the toast would never actually be visible to the user.
const REDIRECT_DELAY_MS = 1500;

function useConsumerAppSwitchEffect(): void {
  const User = useUser();
  const showErnie = useErnie();

  useEffect(() => {
    async function handleAppSwitch(): Promise<void> {
      if (User.isConsumerUser) {
        showErnie(NO_ADMIN_ACCESS_MESSAGE, 'info', REDIRECT_DELAY_MS);
        await new Promise((resolve) => setTimeout(resolve, REDIRECT_DELAY_MS));
        await User.logout();
        window.location.href = consumerRedirectUrl;
      }
    }

    void handleAppSwitch();
  }, [User, User.logout, User.isConsumerUser, showErnie]);
}

export default function RedirectUser(): JSX.Element | null {
  const User = useUser();
  const [query] = useQueryParams(queryParamsConfig);
  const deepLinkSegment = query.deepLinkSegment.startsWith('/') ? query.deepLinkSegment : `/${query.deepLinkSegment}`;

  useConsumerAppSwitchEffect();

  if (User.isSuperAdmin) {
    return <Redirect to='/super' />;
  }

  if (User.isChainAdmin && User.profile.chainID) {
    const to = `/chains/${User.profile.chainID}${deepLinkSegment}`;
    return <Redirect to={to} />;
  }

  if (User.isDispensaryUser && User.profile.dispensaryId) {
    const to = `/dispensaries/${User.profile.dispensaryId}${deepLinkSegment}`;
    return <Redirect to={to} />;
  }

  return null;
}
